// The welcome-survey invitation email, rendered the way the customer receives
// it, with a pencil on each part a company admin can change. It mirrors
// reppi-api's welcome-survey-email.template.ts (the Ninefold design: logo band,
// accent hero with REVIEW & SIGN, branch/manager columns, footer) — keep the
// two in step when either changes.

import React, { useRef } from "react";
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconPencil, IconPhotoPlus, IconTrash } from "@tabler/icons-react";
import "./WelcomeSurveyEmailPreview.css";

// Same fallbacks the email template applies when a company hasn't set its own.
const DEFAULT_ACCENT = "#A3241F";
const DEFAULT_BODY_TEXT =
  "We're excited to serve you and your family and help keep pests and bugs " +
  "away. As a final step, please review and confirm your service agreement " +
  "and subscription details below. If you have any questions or need any " +
  "changes made, just reach out to your sales representative.";

// The pencil (and, for images, the bin) for one editable part. It sits in the
// block's corner by default; `inline` places it in the flow instead, for text
// blocks where an overlay would push the copy into wrapping.
function EditControls({ label, onEdit, onRemove, removeLabel, inline }) {
  return (
    <Group
      gap={4}
      className={inline ? "ws-email-controls-inline" : "ws-email-controls"}
      wrap="nowrap"
    >
      <Tooltip label={label} withinPortal>
        <ActionIcon
          variant="white"
          color="dark"
          size="sm"
          radius="xl"
          aria-label={label}
          onClick={onEdit}
        >
          <IconPencil size={14} />
        </ActionIcon>
      </Tooltip>
      {onRemove && (
        <Tooltip label={removeLabel} withinPortal>
          <ActionIcon
            variant="white"
            color="red"
            size="sm"
            radius="xl"
            aria-label={removeLabel}
            onClick={onRemove}
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
}

// Save / cancel for a part being edited in place, sitting where the finished
// content is so the email doesn't jump around while it's being changed.
function SaveControls({ onSave, onCancel, saving }) {
  return (
    <Group gap="xs" wrap="nowrap">
      <Button size="compact-xs" onClick={onSave} loading={saving}>
        Save
      </Button>
      <Button
        size="compact-xs"
        variant="default"
        onClick={onCancel}
        disabled={saving}
      >
        Cancel
      </Button>
    </Group>
  );
}

// An image in the email: the picture itself when one is set, otherwise the
// `fallback` the email would show in its place (or a click-to-upload
// placeholder when the email would simply leave a gap). Uploading swaps in a
// spinner.
function EmailImage({
  field,
  url,
  alt,
  label,
  className,
  fallback,
  uploading,
  onSelectImage,
  onRemoveImage,
}) {
  const inputRef = useRef(null);

  const pick = () => inputRef.current?.click();
  const handleChange = event => {
    const file = event.currentTarget.files?.[0];
    if (file) onSelectImage(field, file);
    event.currentTarget.value = "";
  };

  const name = alt.toLowerCase();

  return (
    <div className={`ws-email-block ws-email-image-block ${className}`}>
      <input
        type="file"
        accept="image/*"
        ref={inputRef}
        onChange={handleChange}
        style={{ display: "none" }}
      />
      {url ? (
        <img src={url} alt={alt} className="ws-email-image" />
      ) : (
        (fallback ?? (
          <button type="button" className="ws-email-placeholder" onClick={pick}>
            <IconPhotoPlus size={20} />
            <span>{label}</span>
          </button>
        ))
      )}
      {uploading && (
        <div className="ws-email-uploading">
          <Loader size="sm" />
        </div>
      )}
      {(url || fallback) && (
        <EditControls
          label={url ? `Replace ${name}` : label}
          removeLabel={`Remove ${name}`}
          onEdit={pick}
          onRemove={url ? () => onRemoveImage(field) : undefined}
        />
      )}
    </div>
  );
}

function WelcomeSurveyEmailPreview({
  settings,
  accentColor,
  companyName,
  headline,
  bodyText,
  uploadingField,
  onSelectImage,
  onRemoveImage,
  editingHero,
  heroDraft,
  onHeroDraftChange,
  onEditHero,
  onSaveHero,
  onCancelHero,
  editingBranch,
  branchDraft,
  onBranchDraftChange,
  onEditBranch,
  onSaveBranch,
  onCancelBranch,
  editingManager,
  managerDraft,
  onManagerDraftChange,
  onEditManager,
  onSaveManager,
  onCancelManager,
  saving,
}) {
  const accent = accentColor?.trim() || DEFAULT_ACCENT;
  const company = companyName?.trim() || "Your Company";
  const heading = headline?.trim() || `Welcome to the ${company} Family`;
  const body = bodyText?.trim() || DEFAULT_BODY_TEXT;
  const year = new Date().getFullYear();

  const branchLines = [
    settings.branchName,
    settings.branchAddress,
    settings.branchPhone,
    settings.branchEmail,
  ].filter(line => line && line.trim());

  return (
    <div className="ws-email-canvas">
      <div className="ws-email-card">
        {/* Logo band — without a logo the email prints the company name. */}
        <div className="ws-email-logo-band">
          <EmailImage
            field="headerImageUrl"
            url={settings.headerImageUrl}
            alt="Logo"
            label="Add a logo"
            className="ws-email-logo"
            uploading={uploadingField === "headerImageUrl"}
            onSelectImage={onSelectImage}
            onRemoveImage={onRemoveImage}
            fallback={
              <p className="ws-email-wordmark" style={{ color: accent }}>
                {company}
              </p>
            }
          />
        </div>

        {/* Hero */}
        <div className="ws-email-hero" style={{ backgroundColor: accent }}>
          {editingHero ? (
            <Stack gap={8} mb="md" align="stretch">
              <TextInput
                size="xs"
                placeholder={`Welcome to the ${company} Family`}
                value={heroDraft.headline}
                onChange={event =>
                  onHeroDraftChange({ headline: event.currentTarget.value })
                }
                autoFocus
              />
              <Textarea
                size="xs"
                minRows={4}
                autosize
                placeholder={DEFAULT_BODY_TEXT}
                value={heroDraft.body}
                onChange={event =>
                  onHeroDraftChange({ body: event.currentTarget.value })
                }
              />
              <Group justify="center">
                <SaveControls
                  onSave={onSaveHero}
                  onCancel={onCancelHero}
                  saving={saving}
                />
              </Group>
              <Text size="xs" c="white" opacity={0.75} ta="center">
                Leave either blank for the standard wording.
              </Text>
            </Stack>
          ) : (
            <div className="ws-email-hero-copy">
              <p className="ws-email-headline">{heading}</p>
              <p className="ws-email-body">{body}</p>
              <div className="ws-email-hero-copy-edit">
                <EditControls label="Edit email copy" onEdit={onEditHero} />
              </div>
            </div>
          )}

          <EmailImage
            field="emailHeroImageUrl"
            url={settings.emailHeroImageUrl}
            alt="Main image"
            label="Add the main image"
            className="ws-email-hero-photo"
            uploading={uploadingField === "emailHeroImageUrl"}
            onSelectImage={onSelectImage}
            onRemoveImage={onRemoveImage}
          />

          <span className="ws-email-button" style={{ color: accent }}>
            REVIEW &amp; SIGN
          </span>
        </div>

        {/* Branch + manager */}
        <div className="ws-email-contact">
          <div className="ws-email-block ws-email-branch">
            <div className="ws-email-label-row">
              <p className="ws-email-label" style={{ color: accent }}>
                Your Branch
              </p>
              {!editingBranch && (
                <EditControls
                  inline
                  label="Edit branch details"
                  onEdit={onEditBranch}
                />
              )}
            </div>
            {editingBranch ? (
              <Stack gap={6}>
                <TextInput
                  size="xs"
                  placeholder="Branch name"
                  value={branchDraft.branchName}
                  onChange={event =>
                    onBranchDraftChange({
                      branchName: event.currentTarget.value,
                    })
                  }
                  autoFocus
                />
                <TextInput
                  size="xs"
                  placeholder="123 Main St, City, State"
                  value={branchDraft.branchAddress}
                  onChange={event =>
                    onBranchDraftChange({
                      branchAddress: event.currentTarget.value,
                    })
                  }
                />
                <TextInput
                  size="xs"
                  placeholder="+1 (555) 555-5555"
                  value={branchDraft.branchPhone}
                  onChange={event =>
                    onBranchDraftChange({
                      branchPhone: event.currentTarget.value,
                    })
                  }
                />
                <TextInput
                  size="xs"
                  placeholder="branch@example.com"
                  value={branchDraft.branchEmail}
                  onChange={event =>
                    onBranchDraftChange({
                      branchEmail: event.currentTarget.value,
                    })
                  }
                />
                <SaveControls
                  onSave={onSaveBranch}
                  onCancel={onCancelBranch}
                  saving={saving}
                />
              </Stack>
            ) : (
              <>
                {branchLines.length ? (
                  <div className="ws-email-branch-lines">
                    {branchLines.map((line, index) => (
                      <p key={index}>{line}</p>
                    ))}
                  </div>
                ) : (
                  <Text size="sm" c="dimmed">
                    Not set
                  </Text>
                )}
              </>
            )}
          </div>

          <div className="ws-email-block ws-email-manager">
            <p className="ws-email-label" style={{ color: accent }}>
              Your Branch Manager
            </p>
            <EmailImage
              field="managerPhotoUrl"
              url={settings.managerPhotoUrl}
              alt="Manager photo"
              label="Add a photo"
              className="ws-email-manager-photo"
              uploading={uploadingField === "managerPhotoUrl"}
              onSelectImage={onSelectImage}
              onRemoveImage={onRemoveImage}
            />
            {editingManager ? (
              <Stack gap={6} mt="xs">
                <TextInput
                  size="xs"
                  placeholder="Manager name"
                  value={managerDraft}
                  onChange={event =>
                    onManagerDraftChange(event.currentTarget.value)
                  }
                  autoFocus
                />
                <SaveControls
                  onSave={onSaveManager}
                  onCancel={onCancelManager}
                  saving={saving}
                />
              </Stack>
            ) : (
              <>
                <Group gap={6} justify="center" wrap="nowrap" mt="xs">
                  {settings.managerName ? (
                    <p className="ws-email-manager-name">
                      {settings.managerName}
                    </p>
                  ) : (
                    <Text size="sm" c="dimmed">
                      No name
                    </Text>
                  )}
                  <EditControls
                    inline
                    label="Edit manager name"
                    onEdit={onEditManager}
                  />
                </Group>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="ws-email-footer">
          <p>Powered by Reppi &middot; &copy; {year}</p>
        </div>
      </div>
    </div>
  );
}

export default WelcomeSurveyEmailPreview;
